import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Download } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { PageTitle } from "@/components/site/ReleaseCard";
import { euro } from "./Services";
import { CustomerMastering } from "./CustomerMastering";

const STATUS = { awaiting_payment: "In attesa di pagamento", paid: "Pagato — in coda", in_progress: "In lavorazione", delivered: "Consegnato", accepted: "Accettato dall'etichetta", rejected: "Non selezionato", refunded: "Rimborsato" };

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");
  const order = JSON.parse(localStorage.getItem("tga_last_order") || "null");
  useEffect(() => {
    const sid = params.get("session_id");
    let n = 0, stop = false;
    const poll = async () => {
      if (stop || !sid) return setState("error");
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") return setState("paid");
        if (data.status === "expired" || data.payment_status === "failed") return setState("error");
      } catch { return setState("error"); }
      if (++n < 15) setTimeout(poll, 2000); else setState("pending");
    };
    poll();
    return () => { stop = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="max-w-2xl mx-auto px-5 pt-36 text-center" data-testid="payment-success-page">
      {state === "checking" && <p className="inline-flex items-center gap-2 text-zinc-300"><Loader2 className="h-5 w-5 animate-spin" /> Verifica del pagamento…</p>}
      {state === "paid" && <>
        <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto" />
        <h1 className="font-display text-3xl font-black uppercase mt-4" data-testid="payment-success-title">Pagamento riuscito!</h1>
        <p className="text-zinc-300 mt-3">Grazie! Abbiamo ricevuto il tuo ordine. Salva questo link per seguirne lo stato e scaricare il risultato:</p>
        {order && <Link to={`/ordine/${order.id}?t=${order.token}`} className="inline-block mt-5 rounded-full bg-emerald-500 text-[#151827] px-7 py-3 font-bold" data-testid="payment-order-link">Vai al tuo ordine</Link>}
      </>}
      {state === "pending" && <p className="text-amber-400">Pagamento ancora in elaborazione. Ricarica la pagina tra qualche minuto.</p>}
      {state === "error" && <><XCircle className="h-14 w-14 text-hot mx-auto" /><p className="mt-3">Non riusciamo a confermare il pagamento. <Link to="/servizi" className="text-emerald-400 underline">Torna ai servizi</Link></p></>}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-2xl mx-auto px-5 pt-36 text-center" data-testid="payment-cancel-page">
      <h1 className="font-display text-3xl font-black uppercase">Pagamento annullato</h1>
      <p className="text-zinc-300 mt-3">Nessun addebito effettuato. <Link to="/servizi" className="text-emerald-400 underline">Torna ai servizi</Link></p>
    </div>
  );
}

export function OrderStatus() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const [o, setO] = useState(undefined);
  const load = () => api.get(`/orders/${id}`, { params: { t } }).then((r) => setO(r.data)).catch(() => setO(null));
  useEffect(() => { load(); }, [id, t]); // eslint-disable-line react-hooks/exhaustive-deps
  if (o === undefined) return <div className="pt-40 text-center text-zinc-500">Caricamento…</div>;
  if (o === null) return <div className="pt-40 text-center" data-testid="order-not-found">Ordine non trovato.</div>;
  return (
    <div data-testid="order-status-page">
      <PageTitle over="Il tuo ordine" title={o.track_title} sub={`${o.service_title} · ${euro(o.amount)}`} />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 space-y-5">
        <p className="text-xl">Stato: <span className="font-bold text-emerald-300" data-testid="order-status">{STATUS[o.status] || o.status}</span></p>
        {o.has_delivery && <a href={`${BACKEND_URL}/api/orders/${o.id}/download?t=${encodeURIComponent(t)}`} className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-[#151827] px-7 py-3 font-bold" data-testid="order-download-btn"><Download className="h-4 w-4" /> {o.service === "track" ? "Scarica il brano" : "Scarica il master"}</a>}
        {o.service === "mastering" && ["paid", "in_progress", "delivered"].includes(o.status) && <CustomerMastering o={o} t={t} onDelivered={load} />}
      </div>
    </div>
  );
}

export function Terms() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/services").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const l = cfg?.legal || {};
  return (
    <div data-testid="terms-page">
      <PageTitle over="Legale" title="Termini e Condizioni" />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 space-y-4 text-zinc-200">
        <p className="text-sm text-zinc-400">{l.business_name || "[Ragione sociale]"} · P.IVA {l.vat_number || "[P.IVA]"} · {l.address || "[Indirizzo sede]"} · {l.email || "[Email]"}</p>
        <p className="whitespace-pre-line leading-relaxed" data-testid="terms-text">{l.terms_text}</p>
      </div>
    </div>
  );
}
